import { useI18n } from '@upweo/ui';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@upweo/ui';
import type { ProductStatus, ProductSummary } from '@upweo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import * as api from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { PageHeader } from './PageHeader.js';
import { ProductForm } from './ProductForm.js';

const STATUS_VARIANT: Record<ProductStatus, 'success' | 'warning' | 'default'> = {
  ACTIVE: 'success',
  DRAFT: 'warning',
  ARCHIVED: 'default',
};

export const ProductsPage = (): React.JSX.Element => {
  const { t } = useI18n();
  const { activeStore } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProductSummary | null>(null);

  /**
   * The active store is part of the query key. Without it, switching stores
   * would serve the previous tenant's products from cache — the isolation work
   * on the server would be undone by the client.
   */
  const productsQuery = useQuery({
    queryKey: ['products', activeStore?.tenantId, search],
    queryFn: ({ signal }) => api.listProducts({ search, signal }),
    enabled: Boolean(activeStore),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteProduct(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });

  const products = productsQuery.data?.items ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('products.title')}
        subtitle={activeStore ? `${products.length} in ${activeStore.tenantName}` : 'No store'}
        action={
          <Button
            onClick={() => {
              setCreating(true);
              setEditing(null);
            }}
          >
            Add product
          </Button>
        }
      />

      <Input
        type="search"
        placeholder={t('products.searchPlaceholder')}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        className="max-w-sm"
      />

      {creating && (
        <ProductForm
          onClose={() => {
            setCreating(false);
          }}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      )}

      {editing && (
        <ProductForm
          // Remount on a different product so the form's initial state is
          // rebuilt; without the key, React reuses the old field values.
          key={editing.id}
          product={editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      )}

      <TableWrap>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>{t('products.columnProduct')}</TH>
              <TH>{t('common.status')}</TH>
              <TH className="text-right">{t('products.columnPrice')}</TH>
              <TH className="text-right">{t('products.columnStock')}</TH>
              <TH className="text-right">{t('products.columnVariants')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {productsQuery.isPending && <SkeletonRows rows={4} cols={6} />}

            {productsQuery.isError && (
              <tr>
                <td colSpan={6}>
                  <ErrorState
                    message={
                      productsQuery.error instanceof Error
                        ? productsQuery.error.message
                        : 'Failed to load products'
                    }
                  />
                </td>
              </tr>
            )}

            {!productsQuery.isPending && !productsQuery.isError && products.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    title={search ? 'No matching products' : 'No products yet'}
                    description={
                      search
                        ? 'Try a different search term.'
                        : 'Add your first product to start selling.'
                    }
                  />
                </td>
              </tr>
            )}

            {products.map((p) => (
              <TR key={p.id}>
                <TD>
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">
                    /{p.handle}
                    {p.vendor && ` · ${p.vendor}`}
                  </div>
                </TD>
                <TD>
                  <Badge variant={STATUS_VARIANT[p.status]}>{p.status.toLowerCase()}</Badge>
                </TD>
                <TD className="tabular text-right">
                  {p.priceFrom ? api.formatMoney(p.priceFrom, p.currencyCode) : '—'}
                </TD>
                <TD className="tabular text-right">
                  {/* Zero stock is the number a merchant most needs to notice. */}
                  <span className={p.totalAvailable === 0 ? 'text-destructive' : undefined}>
                    {p.totalAvailable}
                  </span>
                </TD>
                <TD className="tabular text-right text-muted-foreground">{p.variantCount}</TD>
                <TD className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(p);
                      setCreating(false);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => {
                      if (window.confirm(`Archive "${p.title}"?`)) {
                        remove.mutate(p.id);
                      }
                    }}
                  >
                    Archive
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
    </div>
  );
};
